import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dbHelpers } from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import StatsCard from './StatsCard'
import RecentTrades from './RecentTrades'
import PerformanceChart from './PerformanceChart'
import * as FiIcons from 'react-icons/fi'

const { FiTrendingUp, FiDollarSign, FiTarget, FiActivity, FiPlus, FiArrowUpRight } = FiIcons

const Dashboard = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [trades, setTrades] = useState([])
  const [stats, setStats] = useState({ totalTrades: 0, winRate: 0, totalPnL: 0, profitFactor: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user) {
      loadDashboardData()
    } else {
      setLoading(false)
    }
  }, [user])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const tradesData = await dbHelpers.getUserTrades(user.id, 10)
      setTrades(tradesData || [])
      
      const totalTrades = tradesData.length
      const winningTrades = tradesData.filter(t => (t.pnl || 0) > 0)
      const losingTrades = tradesData.filter(t => (t.pnl || 0) < 0)
      
      const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0
      const totalPnL = tradesData.reduce((sum, t) => sum + (t.pnl || 0), 0)
      const totalWins = winningTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
      const totalLosses = Math.abs(losingTrades.reduce((sum, t) => sum + (t.pnl || 0), 0))
      
      const profitFactor = totalLosses > 0 ? totalWins / totalLosses : (totalWins > 0 ? 99.9 : 0)
      
      setStats({ totalTrades, winRate, totalPnL, profitFactor })
    } catch (error) {
      console.error('Dashboard data error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-lux-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-20">
      {/* Welcome & Main Balance Area */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-8"
      >
        <div className="lg:col-span-2 space-y-8">
          <div className="flex justify-between items-end">
            <div>
              <p className="text-gray-500 dark:text-gray-400 font-medium mb-1">Total Balance</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white tracking-tight">
                Rp {Math.abs(stats.totalPnL).toLocaleString('id-ID')}
                
              </h1>
              <div className={`flex items-center mt-3 space-x-2 ${stats.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                <div className={`p-1 rounded-full ${stats.totalPnL >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  <SafeIcon icon={FiArrowUpRight} className="h-4 w-4" />
                </div>
                <span className="font-bold">+Rp {Math.abs(stats.totalPnL).toLocaleString('id-ID')} (12%)</span>
              </div>
            </div>
            
            <button 
              onClick={() => navigate('/journal', { state: { openForm: true } })}
              className="hidden sm:flex items-center space-x-2 lux-button-primary"
            >
              <SafeIcon icon={FiPlus} className="h-5 w-5" />
              <span>New Trade</span>
            </button>
          </div>

          {/* Main Chart Area */}
          <div className="h-80 w-full bg-gradient-to-b from-transparent to-lux-primary/5 rounded-3xl border border-lux-light-border dark:border-lux-dark-border p-1 relative overflow-hidden group">
             <div className="absolute inset-0 bg-lux-primary/5 blur-3xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
             <PerformanceChart trades={trades} />
          </div>
        </div>

        {/* Quick Stats Column */}
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-bold text-gray-900 dark:text-white">Overview</h3>
            <button className="text-sm text-lux-primary font-medium">View All</button>
          </div>
          <StatsCard 
            title="Win Rate" 
            value={stats.winRate} 
            icon={FiTarget} 
            color="green" 
            format="percentage" 
            trend="+2.4%"
          />
          <StatsCard 
            title="Profit Factor" 
            value={stats.profitFactor} 
            icon={FiTrendingUp} 
            color="blue" 
            format="decimal"
          />
          <StatsCard 
            title="Total Trades" 
            value={stats.totalTrades} 
            icon={FiActivity} 
            color="purple" 
            format="number"
          />
          
          {/* Mobile Action Button */}
          <button 
            onClick={() => navigate('/journal', { state: { openForm: true } })}
            className="w-full sm:hidden lux-button-primary flex justify-center items-center space-x-2 mt-4"
          >
            <SafeIcon icon={FiPlus} className="h-5 w-5" />
            <span>Add New Trade</span>
          </button>
        </div>
      </motion.div>

      {/* Recent Activity */}
      <div className="pt-4">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Recent Activity</h3>
        <RecentTrades trades={trades} />
      </div>
    </div>
  )
}

export default Dashboard